import {
  setOrganizationId,
  setOrganizationPlanId,
  setShowtimeDomain,
  useGetCurrentUserQuery,
  useGetOrganizationQuery,
} from '@carboncopyco/data';
import { WindowType } from '@carboncopyco/types/desktop';
import { AnalyticsTrackEventName, useAnalytics } from '@carboncopyco/utils';
import { useFlags, useLDClient } from 'launchdarkly-react-client-sdk';
import { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import packageJson from '../../../package.json';
import { settings } from '../../../settings';
import TabsContainer from '../components/global-tabs/TabsContainer';
import useAsyncMemo from '../hooks/useAsyncMemo';
import { useUser } from '../store/context/UserContext';
import { useCustomDomain } from 'renderer/hooks/useCustomDomain';

function MainScreen() {
  const { activeOrganization } = useUser();
  const ldClient = useLDClient();
  const flags = useFlags();
  const dispatch = useDispatch();
  const { data: appVersion } = useAsyncMemo(
    window?.dsDesktop?.app?.appVersion,
    []
  );

  const organizationId = activeOrganization?.id;

  const { data: organization } = useGetOrganizationQuery(
    { id: organizationId?.toString() },
    { skip: !organizationId }
  );
  const { data: userData } = useGetCurrentUserQuery();

  const { email, name, lastName, firstName, intercomUserHash, id, roles } =
    userData?.user || {
      email: '',
      name: '',
      lastName: '',
      firstName: '',
      id: '',
    };

  const { identify, trackEvent, init: initAnalytics } = useAnalytics();

  const eventListenerSetUp = () => {
    window.dsDesktop.app.updateMenuResetStates(true);
    window.dsDesktop.view.resizeByType(WindowType.MAIN);
  };

  useEffect(() => {
    eventListenerSetUp();
  }, []);
  const customDomain = useCustomDomain(organization);
  useEffect(() => {
    if (!organization || !userData || !appVersion) return;
    dispatch(setOrganizationPlanId(organization.planId));
    dispatch(setShowtimeDomain(customDomain));
    dispatch(setOrganizationId(organizationId.toString()));

    initAnalytics({
      organization,
      userData: { firstName, lastName, email, name, id },
      source: settings.SEGMENT_SOURCE,
      version: appVersion,
      withHubspot: flags?.withHubspot,
    });

    try {
      const role = roles?.[organization.id];
      const identity = {
        name,
        email,
        role,
        sf_account: organization?.sfAccount,
        organization_plan_id: organization.planId,
        organization_id: organization.id,
        organization_name: organization.organizationName,
        organization_slug: organization.slugName,
        organization_state: organization.state,
        intercom_user_hash: intercomUserHash,
        platform: 'desktop',
        desktop_app_version: appVersion,
        os: (navigator as any)?.userAgentData?.platform,
      };

      identify(email, identity, {
        integrations: {
          Intercom: {
            company: {
              id: organization?.sfAccount,
              name: organization.organizationName,
            },
            user_hash: intercomUserHash,
            role,
            name,
            organization_id: organization.id,
          },
        },
      });

      trackEvent(AnalyticsTrackEventName.DESKTOPAPP_LOGIN, {
        location: 'login',
      });
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('Can not init analytics', e);
    }
  }, [activeOrganization, userData, organization, appVersion]);

  useEffect(() => {
    if (!activeOrganization) return;
    ldClient
      ?.identify({
        key: email,
        email,
        custom: {
          org: activeOrganization.organization_name,
          organizationName: activeOrganization.organization_name,
          organizationId: activeOrganization.id,
          organizationSlug: activeOrganization.slug_name,
          platformType: 'desktop-app',
          versionNumber: packageJson.version,
        },
      })
      .catch((e) => {
        // eslint-disable-next-line no-console
        console.error('Can not init ldClient', e);
      });
  }, [activeOrganization?.id]);

  return <TabsContainer />;
}

export default MainScreen;
